import { formatLapTime } from '@racedash/shared'
import { Splits, Tile } from '../Tile'
import type { WidgetProps } from './props'

/**
 * Sector times for the lap in progress.
 *
 * The interpreter writes each sector into the lap document as it lands rather
 * than only when the lap completes, so this fills in a third at a time. That is
 * the point of the widget: on a three-minute lap, waiting for the line means
 * knowing nothing for three minutes.
 *
 * A sector set under a yellow or in a slow zone is marked, because it is not
 * comparable with a green one, and showing it as though it were would have a
 * driver chasing a time they cannot have.
 *
 * Each sector carries this car's session best underneath. Without it the
 * running figure is a number with nothing to be measured against, and the
 * comparison is the reason anybody looks: forty-four seconds means nothing,
 * forty-four against a best of forty-three means half a second.
 *
 * No colour on it, deliberately. Deciding whether a sector is quick is exactly
 * what a purple-green-yellow scheme asserts, and that judgement is harder than
 * it looks — personal best, session best and class best are three different
 * claims, and a tile that got it wrong would be read at a glance and believed.
 * The numbers are here; the judgement is the reader's until we can make it
 * properly.
 */
export default function Sectors({ car, data }: WidgetProps<'sectors'>) {
  // Laps arrive newest first, so this is the lap in progress where there is
  // one, and the last completed lap otherwise.
  const lap = data.laps[0]
  const inProgress = lap != null && (lap.lapTimeMs == null || lap.lapTimeMs <= 0)

  const value = (ms: number | null | undefined) => (ms == null || ms <= 0 ? '—' : formatLapTime(ms))

  /*
   * The best, or a space holding its place.
   *
   * An em dash where there is no best yet, rather than nothing at all: three
   * columns that grow a second line at different moments would have the tile
   * resettling under somebody reading it.
   */
  const best = (ms: number | null | undefined) =>
    ms == null || ms <= 0 ? 'best —' : `best ${formatLapTime(ms)}`

  const s = data.carSummary
  const label = !car || !lap ? 'Sectors' : inProgress ? `Lap ${lap.lap} · running` : `Lap ${lap.lap}`

  return (
    <Tile label={label}>
      <Splits
        items={[
          {
            label: 'S1',
            value: value(lap?.s1Ms),
            flagged: lap?.s1Fcy || lap?.s1Sz,
            sub: best(s?.bestS1Ms),
          },
          {
            label: 'S2',
            value: value(lap?.s2Ms),
            flagged: lap?.s2Fcy || lap?.s2Sz,
            sub: best(s?.bestS2Ms),
          },
          {
            label: 'S3',
            value: value(lap?.s3Ms),
            flagged: lap?.s3Fcy || lap?.s3Sz,
            sub: best(s?.bestS3Ms),
          },
        ]}
      />
    </Tile>
  )
}
